<template>
  <CCard>
    <CCardHeader class="d-flex justify-content-between align-items-center card-header">
      <h2
        v-if="!id"
        class="mb-0"
      >
        New Instructor
      </h2>
      <h2
        v-else
        class="mb-0"
      >
        Edit Instructor
      </h2>

      <CRow class="align-items-center card-header__row">
        <CSelect
          class="mb-0 mr-3"
          :value="current_language"
          :options="[
            { label: 'English', value: 'en' },
            { label: 'Spanish', value: 'es' },
            { label: 'German', value: 'de' },
            { label: 'Dutch', value: 'nl' },
            { label: 'Italian', value: 'it' },
            { label: 'French', value: 'fr' },
          ]"
          @update:value="updateLanguage"
        />

        <CButton
          color="primary"
          @click="resetPassword"
        >
          Reset Password
        </CButton>
      </CRow>
    </CCardHeader>
    <CCardBody>
      <form
        v-if="!id || !user_loading"
        autocomplete="off"
        @submit.prevent="submit"
      >
        <CInput
          v-model="new_instructor.first_name"
          label="First Name"
          autocomplete="first-name"
          :add-input-classes="{ 'is-invalid': hasError('first_name') }"
          :invalid-feedback="getError('first_name')"
        />
        <CInput
          v-model="new_instructor.last_name"
          label="Last Name"
          autocomplete="last-name"
          :add-input-classes="{ 'is-invalid': hasError('last_name') }"
          :invalid-feedback="getError('last_name')"
        />
        <CInput
          v-model="new_instructor.email"
          label="Email"
          autocomplete="email"
          :disabled="!isEnglish"
          :add-input-classes="{ 'is-invalid': hasError('email') }"
          :invalid-feedback="getError('email')"
        />
        <CTextarea
          v-model="new_instructor.description"
          label="Instructor Biography"
          autocomplete="description"
          :add-input-classes="{ 'is-invalid': hasError('description') }"
          :invalid-feedback="getError('description')"
        />

        <div class="row my-3">
          <div class="col-md-6">
            <img
              :src="new_instructor.image"
              alt=""
              width="300rem"
            >
          </div>

          <div class="col-md-6">
            <div class="row">
              <div class="col-md-6">
                <h4>Original Image</h4>

                <cropper
                  v-if="image.src"
                  ref="cropper"
                  class="upload-example-cropper"
                  :src="image.src"
                  @change="onChange"
                />

                <div>
                  <CButton
                    class="button"
                    type="button"
                    @click="$refs.file.click()"
                  >
                    <input
                      ref="file"
                      type="file"
                      accept="image/*"
                      @change="loadImage($event)"
                    >
                    Load image
                  </CButton>
                </div>

                <div
                  v-if="image.src"
                  class="mt-3"
                >
                  <CButton
                    class="button"
                    type="button"
                    color="secondary"
                    @click="reset"
                  >
                    Clear selected image
                  </CButton>
                  <small class="text-muted d-block mt-1">
                    This only removes the photo waiting to be uploaded.
                  </small>
                </div>
              </div>

              <div class="col-md-6">
                <h4>Cropped Image</h4>
                <preview
                  v-if="result.image"
                  :image="result.image"
                  style="width: 10rem; height: 10rem"
                  :coordinates="result.coordinates"
                />
              </div>
            </div>
          </div>
        </div>

        <CButton
          type="submit"
          color="primary"
          :disabled="!isEdited"
        >
          Save
        </CButton>
      </form>
      <div
        v-else
        class="mb-3"
      >
        <CSpinner
          style="width: 2rem; height: 2rem"
          color="primary"
          grow
        />
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import hasApiValidation from '@/mixins/hasApiValidation';
import { CButton } from '@coreui/vue-pro';
import { Cropper, Preview } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

function getMimeType(file, fallback = null) {
  const byteArray = new Uint8Array(file).subarray(0, 4);
  let header = '';
  for (let i = 0; i < byteArray.length; i++) {
    header += byteArray[i].toString(16);
  }
  switch (header) {
    case '89504e47':
      return 'image/png';
    case '47494638':
      return 'image/gif';
    case 'ffd8ffe0':
    case 'ffd8ffe1':
    case 'ffd8ffe2':
    case 'ffd8ffe3':
    case 'ffd8ffe8':
      return 'image/jpeg';
    default:
      return fallback;
  }
}

export default {
  name: 'EditInstructor',
  components: {
    Cropper,
    Preview,
  },
  mixins: [hasApiValidation],
  beforeRouteLeave(to, from, next) {
    if (this.isEdited) {
      const answer = window.confirm(
        'You have unsaved changes. Are you sure you want to leave the page?',
      );
      if (answer) {
        return next();
      } else {
        return next(false);
      }
    } else {
      return next();
    }
  },
  props: ['id'],
  data() {
    return {
      result: {
        coordinates: null,
        image: null,
      },
      image: {
        src: null,
        type: null,
      },
      current_language: 'en',
      roles: [],
      new_instructor: {
        first_name: '',
        last_name: '',
        email: '',
        description: '',
        image: '',
        image_base64: null,
      },
      loading_roles: true,
      user_loading: false,
      translations: {},
      originData: {},
    };
  },
  computed: {
    isEnglish() {
      return this.current_language === 'en';
    },
    isEdited() {
      const parser = (data) => JSON.stringify(data);
      return parser(this.originData) !== parser(this.new_instructor);
    },
  },
  mounted() {
    this.user_loading = true;
    this.fetchUser(this.id);
  },
  unmounted() {
    if (this.image.src) {
      URL.revokeObjectURL(this.image.src);
    }
  },
  methods: {
    reset() {
      this.image = {
        src: null,
        type: null,
      };
      this.result = {
        coordinates: null,
        image: null,
      };
      this.new_instructor.image_base64 = null;
    },
    onChange({ coordinates, image }) {
      this.result = {
        coordinates,
        image,
      };
      const { canvas } = this.$refs.cropper.getResult();
      const base64Image = canvas.toDataURL(this.image.type);

      this.new_instructor.image_base64 = base64Image;
    },
    loadImage(event) {
      const { files } = event.target;

      if (files && files[0]) {
        if (this.image.src) {
          URL.revokeObjectURL(this.image.src);
        }

        const blob = URL.createObjectURL(files[0]);
        const reader = new FileReader();

        reader.onload = (e) => {
          this.image = {
            src: blob,
            type: getMimeType(e.target.result, files[0].type),
          };
        };
        reader.readAsArrayBuffer(files[0]);
      }
    },
    setOriginData() {
      this.originData = JSON.parse(JSON.stringify(this.new_instructor));
    },
    setUserData(data) {
      if (this.isEnglish) {
        this.new_instructor.email = data.data.email;
      }

      this.new_instructor.id = data.data.id;
      this.new_instructor.first_name = data.data.first_name;
      this.new_instructor.last_name = data.data.last_name;
      this.new_instructor.description = data.data.description;
      this.translations = {
        ...data.data.translations,
        en: {
          first_name: data.data.first_name,
          last_name: data.data.last_name,
          description: data.data.description,
        },
      };

      this.new_instructor.image = decodeURIComponent(data.data.image);

      console.log('url: ' + data.data.image);

      this.setOriginData();
    },
    fetchUser(id) {
      this.$http
        .get('/instructors/' + id)
        .then(({ data }) => {
          this.setUserData(data);
        })
        .finally(() => {
          this.user_loading = false;
        });
    },
    updateData(data) {
      this.new_instructor.roles = data;
    },
    updateLanguage(value) {
      const prevLanguage = this.current_language;

      if (this.isEdited) {
        const answer = window.confirm(
          'You have unsaved changes! Are you sure you want to leave the page?',
        );
        if (!answer) {
          this.current_language = null;
          this.$nextTick(() => {
            this.current_language = prevLanguage;
          });
          return;
        }
      }

      this.current_language = value;

      const currentLanguageData = this.translations[value] || {};

      this.new_instructor.first_name = currentLanguageData?.first_name || '';
      this.new_instructor.last_name = currentLanguageData?.last_name || '';
      this.new_instructor.description = currentLanguageData?.description || '';

      this.setOriginData();
    },
    submit() {
      this.update();
    },
    resetPassword() {
      this.$http
        .put('/instructors/' + this.id + '/password')
        .then((data) => {
          this.$toast.success("New password has been sent to the user's email");
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
        });
    },
    update() {
      this.setErrors({});
      let action;
      if (this.isEnglish) {
        action = this.$http.put('/instructors/' + this.id, this.new_instructor);
      } else {
        action = this.$http.post(`/instructors/${this.id}/update-or-create-translations`, {
          language: this.current_language,
          first_name: this.new_instructor.first_name,
          last_name: this.new_instructor.last_name,
          description: this.new_instructor.description,
        });
      }

      action
        .then(({ data }) => {
          this.setUserData(data);
          this.updateLanguage(this.current_language);
          this.$toast.success('Changes have been saved successfully');
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
          this.setErrors(response.data.errors);
        });
    },
  },
};
</script>

<style scoped>
.upload-example-cropper {
  border: solid 1px #eee;
  /* min-height: 300px;
  max-height: 500px;  */
  width: 100%;
}

.button {
  color: white;
  font-size: 16px;
  padding: 10px 20px;
  background: #007bff;
  cursor: pointer;
  transition: background 0.5s;
  width: 100%;
  text-align: center;
}

.button:hover {
  background: #1d5ca0;
}

.button input {
  display: none;
}
</style>
